// Centralized UI theme. Single source of truth for colors, fonts, and depth
// layers. Per docs/UI_REDESIGN_PLAN.md §1. Plain string/number constants so
// existing Phaser style objects keep their shape.

import Phaser from "phaser";

export const FONT_DISPLAY = "'Cinzel', 'Trebuchet MS', serif";
export const FONT_BODY = "'IM Fell English', 'Trebuchet MS', serif";

export const THEME = {
  colors: {
    parchment:    "#f2e2b8",
    parchmentDim: "#cfc0a0",
    gold:         "#ffd66b",
    goldDim:      "#b8923a",
    iron:         "#2a1d10",
    ironDeep:     "#1a1108",
    ironEdge:     "#8a6a3a",
    blood:        "#c04040",
    mana:         "#5aa9ff",
    forge:        "#ff8855",
    frost:        "#9dd6ff",
    lock:         "#604838"
  } as const,
  hex: {
    iron:     0x2a1d10,
    ironDeep: 0x1a1108,
    ironEdge: 0x8a6a3a,
    gold:     0xffd66b,
    goldDim:  0xb8923a,
    blood:    0xc04040,
    mana:     0x5aa9ff,
    forge:    0xff8855,
    frost:    0x9dd6ff,
    lock:     0x604838
  } as const,
  fonts: {
    display: FONT_DISPLAY,
    body:    FONT_BODY
  } as const,
  depths: {
    world:    0,
    units:    100,
    effects:  500,
    hud:      800,
    boss:     900,
    overlay:  1000,
    toast:    1500,
    tooltip:  2000
  } as const
};

/* -------------------------------------------------------------------------- */
/*                              Panel + button helpers                        */
/* -------------------------------------------------------------------------- */

export type PanelVariant = "iron" | "parchment" | "iron-bar" | "iron-thin";

const FRAME_KEYS: Record<PanelVariant, { key: string; corner: number; srcW: number; srcH: number }> = {
  "iron":        { key: "frame-iron",       corner: 16, srcW: 256, srcH: 256 },
  "parchment":   { key: "frame-parchment",  corner: 16, srcW: 256, srcH: 256 },
  "iron-bar":    { key: "frame-iron-bar",   corner: 16, srcW: 512, srcH: 96 },
  "iron-thin":   { key: "frame-iron-thin",  corner: 8,  srcW: 512, srcH: 32 }
};

/**
 * Returns a 9-slice panel anchored at top-left (origin 0,0) for predictable
 * layout. Use this everywhere panels are needed instead of add.rectangle().
 */
export function panel(
  scene: Phaser.Scene,
  x: number, y: number, w: number, h: number,
  variant: PanelVariant = "iron"
): Phaser.GameObjects.NineSlice {
  const def = FRAME_KEYS[variant];
  const ns = scene.add.nineslice(x, y, def.key, undefined, w, h, def.corner, def.corner, def.corner, def.corner);
  ns.setOrigin(0);
  return ns;
}

export interface ButtonOptions {
  variant?: PanelVariant;            // default "iron"
  fontSize?: number;                 // default 16
  color?: string;                    // text color
  display?: boolean;                 // use display font instead of body
  onClick?: () => void;
}

/**
 * Iron-framed text button with hover micro-tween (scale 1 → 1.04, gold tint).
 * Returns a Container; .getData("label") for the inner Text if needed.
 */
export function button(
  scene: Phaser.Scene,
  x: number, y: number, w: number, h: number,
  text: string,
  opts: ButtonOptions = {}
): Phaser.GameObjects.Container {
  const variant = opts.variant ?? "iron";
  const c = scene.add.container(x, y);
  const bg = panel(scene, -w / 2, -h / 2, w, h, variant);
  bg.setInteractive({ useHandCursor: true });
  const label = scene.add.text(0, 0, text, {
    fontFamily: opts.display ? THEME.fonts.display : THEME.fonts.body,
    fontSize: `${opts.fontSize ?? 16}px`,
    color: opts.color ?? THEME.colors.gold
  }).setOrigin(0.5);
  c.add([bg, label]);
  c.setData("bg", bg);
  c.setData("label", label);

  const baseColor = opts.color ?? THEME.colors.gold;
  bg.on("pointerover", () => {
    scene.tweens.add({ targets: c, scale: 1.04, duration: 80, ease: "Sine.easeOut" });
    label.setColor(THEME.colors.parchment);
  });
  bg.on("pointerout", () => {
    scene.tweens.add({ targets: c, scale: 1.0, duration: 100, ease: "Sine.easeOut" });
    label.setColor(baseColor);
  });
  if (opts.onClick) bg.on("pointerdown", opts.onClick);
  return c;
}

/* -------------------------------------------------------------------------- */
/*                          Achievement tier styling                          */
/* -------------------------------------------------------------------------- */

export type AchievementTierKey = "bronze" | "silver" | "gold" | "platinum";

export const TIER_STYLE: Record<AchievementTierKey, {
  hex: number;
  numeral: string;
  nameColor: string;
}> = {
  bronze:   { hex: 0xcd7f32, numeral: "I",   nameColor: "#e8c87a" },
  silver:   { hex: 0xb8c4c8, numeral: "II",  nameColor: "#d8e4e8" },
  gold:     { hex: 0xffd66b, numeral: "III", nameColor: "#ffd66b" },
  platinum: { hex: 0xc8b8ff, numeral: "IV",  nameColor: "#e0d0ff" }
};

